import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { map, catchError, switchMap, tap } from 'rxjs/operators';
import { CompaniesRepository } from '../../infrastructure/http/repositories/companies.repository';
import { LoggingService } from '../../core/services/logging.service';
import {
  CompaniesPageActions,
  CompaniesAPIActions,
  CompanyDetailPageActions,
  CompanyDetailAPIActions,
  CompanyFormActions,
  CompanyFormAPIActions,
} from './companies.actions';
import {
  selectCurrentPage,
  selectPageSize,
  selectSearchTerm,
  selectSortBy,
  selectSortOrder,
} from './companies.selectors';

@Injectable()
export class CompaniesEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly router = inject(Router);
  private readonly repository = inject(CompaniesRepository);
  private readonly logger = inject(LoggingService);

  loadCompanies$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        CompaniesPageActions.loadCompanies,
        CompaniesPageActions.changePage,
        CompaniesPageActions.changePageSize,
        CompaniesPageActions.searchCompanies,
        CompaniesPageActions.sort
      ),
      concatLatestFrom(() => [
        this.store.select(selectCurrentPage),
        this.store.select(selectPageSize),
        this.store.select(selectSearchTerm),
        this.store.select(selectSortBy),
        this.store.select(selectSortOrder),
      ]),
      switchMap(([, page, pageSize, search, sortBy, sortOrder]) => {
        this.logger.debug('Loading companies', {
          page,
          pageSize,
          search,
          sortBy,
          sortOrder,
        });
        return this.repository
          .getCompanies({ page, pageSize, search, sortBy, sortOrder })
          .pipe(
            map(result => {
              this.logger.debug('Companies loaded', {
                totalCount: result.totalCount,
              });
              return CompaniesAPIActions.loadCompaniesSuccess({ result });
            }),
            catchError(error => {
              this.logger.error('Failed to load companies', error);
              return of(
                CompaniesAPIActions.loadCompaniesFailure({
                  error: 'Failed to load companies.',
                })
              );
            })
          );
      })
    )
  );

  loadCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyDetailPageActions.loadCompany),
      switchMap(({ arNumber }) => {
        this.logger.debug('Loading company', { arNumber });
        return this.repository.getByArNumber(arNumber).pipe(
          map(company => {
            if (company) {
              return CompanyDetailAPIActions.loadCompanySuccess({ company });
            } else {
              return CompanyDetailAPIActions.loadCompanyNotFound({ arNumber });
            }
          }),
          catchError(error => {
            this.logger.error('Failed to load company', error);
            return of(
              CompanyDetailAPIActions.loadCompanyFailure({
                error: 'Failed to load company.',
              })
            );
          })
        );
      })
    )
  );

  createCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyFormActions.createCompany),
      switchMap(({ request }) => {
        this.logger.debug('Creating company', { arNumber: request.arNumber });
        return this.repository.create(request).pipe(
          map(company => {
            this.logger.debug('Company created', {
              arNumber: company.arNumber,
            });
            return CompanyFormAPIActions.createCompanySuccess({ company });
          }),
          catchError(error => {
            this.logger.error('Failed to create company', error);
            return of(
              CompanyFormAPIActions.createCompanyFailure({
                error: 'Failed to create company.',
              })
            );
          })
        );
      })
    )
  );

  createCompanySuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(CompanyFormAPIActions.createCompanySuccess),
        tap(() => {
          this.router.navigate(['/company']);
        })
      ),
    { dispatch: false }
  );

  updateCompany$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyFormActions.updateCompany),
      switchMap(({ arNumber, request }) => {
        this.logger.debug('Updating company', { arNumber });
        return this.repository.update(arNumber, request).pipe(
          switchMap(company => {
            this.logger.debug('Company updated', { arNumber });
            // API returns 204 No Content on success, so company may be null
            // Reload the company to get the updated data
            if (!company) {
              return this.repository.getByArNumber(arNumber).pipe(
                map(reloadedCompany => {
                  if (reloadedCompany) {
                    return CompanyFormAPIActions.updateCompanySuccess({
                      company: reloadedCompany,
                    });
                  } else {
                    return CompanyFormAPIActions.updateCompanyFailure({
                      error: 'Failed to reload company after update.',
                    });
                  }
                })
              );
            }
            return of(CompanyFormAPIActions.updateCompanySuccess({ company }));
          }),
          catchError(error => {
            this.logger.error('Failed to update company', error);
            return of(
              CompanyFormAPIActions.updateCompanyFailure({
                error: 'Failed to update company.',
              })
            );
          })
        );
      })
    )
  );

  searchSalesforce$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CompanyFormActions.searchSalesforce),
      switchMap(({ query }) => {
        this.logger.debug('Searching Salesforce', { query });
        return this.repository.searchSalesforceCompanies(query).pipe(
          map(results => {
            return CompanyFormAPIActions.salesforceSearchSuccess({ results });
          }),
          catchError(error => {
            this.logger.error('Failed to search Salesforce', error);
            return of(
              CompanyFormAPIActions.salesforceSearchFailure({
                error: 'Failed to search.',
              })
            );
          })
        );
      })
    )
  );
}
